import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, apiErr, API } from "@/lib/api";
import { DashShell, StatusTag } from "@/components/dash";
import ListingCard from "@/components/ListingCard";
import { toast } from "sonner";
import { Download, ShoppingBag, Heart, MessageSquareWarning, Star, Loader2, Printer, ArrowRight, MapPin, Clock, XCircle, CheckCircle2, PackageCheck, Truck, ShieldCheck, UserCog } from "lucide-react";
import ProfilePanel from "@/components/ProfilePanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import PrintToRealityWizard from "@/components/PrintToRealityWizard";
import PrintJobChat from "@/components/PrintJobChat";
import useRazorpay from "@/hooks/useRazorpay";
import { logTermsCheckpoint } from "@/lib/termsCheckpoint";

const NAV = [
  { key: "purchases", label: "My Purchases", icon: ShoppingBag },
  { key: "print_jobs", label: "Print Jobs", icon: Printer },
  { key: "wishlist", label: "Wishlist", icon: Heart },
  { key: "disputes", label: "Disputes", icon: MessageSquareWarning },
  { key: "profile", label: "Profile", icon: UserCog },
];

const JOB_STATUS_STYLE = {
  new:       "bg-blue-500/15 text-blue-500 border-blue-500/30",
  accepted:  "bg-indigo-500/15 text-indigo-500 border-indigo-500/30",
  quoted:    "bg-amber-500/15 text-amber-600 border-amber-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  printing:  "bg-purple-500/15 text-purple-500 border-purple-500/30",
  ready:     "bg-teal-500/15 text-teal-500 border-teal-500/30",
  shipped:   "bg-sky-500/15 text-sky-500 border-sky-500/30",
  completed: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  declined:  "bg-red-500/15 text-red-500 border-red-500/30",
  cancelled: "bg-gray-500/15 text-gray-500 border-gray-500/30",
};

export default function BuyerDashboard() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "purchases";
  const setTab = (t) => setParams({ tab: t });
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [ptrOrder, setPtrOrder] = useState(null); // order for which the wizard is open

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/wishlist").then((r) => setWishlist(r.data)).catch(() => {});
    api.get("/disputes/mine").then((r) => setDisputes(r.data));
    api.get("/buyer/print-jobs").then((r) => setPrintJobs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const download = async (order, fmt) => {
    try {
      // Mint a short-lived signed URL (15 min TTL, buyer-locked) and
      // navigate the browser to it directly — no bearer token in flight,
      // no long-lived shareable link. If the buyer forwards the URL,
      // it stops working the moment the 15-minute clock ticks over.
      const { data } = await api.post(`/download/${order.id}/link${fmt ? `?fmt=${fmt}` : ""}`);
      if (!data?.url) throw new Error("no url");
      window.location.assign(data.url);
      toast.success("Download link ready — expires in 15 min");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <DashShell title="Buyer Dashboard" subtitle="Purchases, downloads & disputes" nav={NAV} active={active} onTab={setTab}>
      <PrintToRealityWizard
        order={ptrOrder}
        open={!!ptrOrder}
        onClose={() => setPtrOrder(null)}
        onCreated={() => { setTab("print_jobs"); load(); }}
      />
      {active === "purchases" && (
        <div className="space-y-3">
          {/* Print-to-Reality Coming Soon — surfaces the value while we onboard vendors */}
          <PrintToRealityTeaser />
          {orders.length === 0 && <Empty text="No purchases yet." />}
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-border rounded-md bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{o.listing_title}</p>
                  {o.product_id && (
                    <span
                      data-testid={`order-pid-${o.id}`}
                      className="font-mono text-[10px] tracking-widest uppercase bg-secondary text-muted-foreground px-2 py-0.5 rounded font-bold"
                      title="Jeweltix Product ID"
                    >
                      {o.product_id}
                    </span>
                  )}
                </div>
                <p className="font-mono text-xs text-muted-foreground mt-1">Order {o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString()} · {inr(o.amount)}</p>
              </div>
              <StatusTag status={o.status} />
              <div className="flex gap-2">
                <button onClick={() => download(o)} data-testid={`download-${o.id}`} disabled={o.status === "refunded"} className="flex items-center gap-1.5 bg-primary text-primary-foreground rounded-md px-3 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50">
                  <Download className="h-4 w-4" /> {o.has_3dm ? "3DM" : "STL"}
                </button>
                {o.has_stl && o.has_3dm && (
                  <button onClick={() => download(o, "stl")} data-testid={`download-stl-${o.id}`} disabled={o.status === "refunded"} className="flex items-center gap-1.5 border border-border rounded-md px-3 py-2 text-sm hover:border-primary disabled:opacity-50">
                    <Download className="h-4 w-4" /> STL
                  </button>
                )}
                <ReviewButton order={o} onDone={load} />
                {o.status !== "refunded" && (
                  <button
                    onClick={() => setPtrOrder(o)}
                    data-testid={`print-to-reality-${o.id}`}
                    className="flex items-center gap-1.5 border border-emerald-500/40 text-emerald-600 rounded-md px-3 py-2 text-sm font-medium hover:bg-emerald-500/5"
                  >
                    <Printer className="h-4 w-4" /> Print
                  </button>
                )}
                {!o.dispute_id && o.status !== "refunded" && <DisputeButton order={o} onDone={load} />}
              </div>
            </div>
          ))}
        </div>
      )}

      {active === "print_jobs" && (
        <PrintJobsTab jobs={printJobs} onReload={load} />
      )}

      {active === "wishlist" && (
        wishlist.length === 0 ? <Empty text="Your wishlist is empty." /> :
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">{wishlist.map((l) => <ListingCard key={l.id} listing={l} />)}</div>
      )}

      {active === "disputes" && (
        <div className="space-y-3">
          {disputes.length === 0 && <Empty text="No disputes raised." />}
          {disputes.map((d) => (
            <div key={d.id} className="border border-border rounded-md bg-card p-4">
              <div className="flex items-center justify-between">
                <p className="font-semibold">{d.listing_title}</p>
                <StatusTag status={d.status} />
              </div>
              <p className="font-mono text-xs text-muted-foreground mt-1">{d.issue_type} · {new Date(d.created_at).toLocaleDateString()}</p>
              <p className="text-sm text-muted-foreground mt-2">{d.description}</p>
              {d.resolution && <p className="text-sm mt-2 text-emerald-600">Resolved: {d.resolution.replace("_", " ")} {d.resolution_note ? `— ${d.resolution_note}` : ""}</p>}
            </div>
          ))}
        </div>
      )}

      {active === "profile" && <ProfilePanel />}
    </DashShell>
  );
}

function Empty({ text }) {
  return <div className="border border-dashed border-border rounded-md py-20 text-center text-muted-foreground">{text}</div>;
}

function ReviewButton({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try {
      await api.post("/reviews", { order_id: order.id, rating, comment });
      toast.success("Review posted"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button className="flex items-center gap-1.5 border border-border rounded-md px-3 py-2 text-sm hover:border-primary"><Star className="h-4 w-4" /> Review</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Review "{order.listing_title}"</DialogTitle></DialogHeader>
        <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)}><Star className={`h-7 w-7 ${n <= rating ? "fill-amber-500 text-amber-500" : "text-muted-foreground"}`} /></button>)}</div>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="w-full border border-border rounded-md p-3 text-sm bg-card" rows={3} />
        <DialogFooter><button onClick={submit} data-testid="submit-review" className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-medium">Post review</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeButton({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("corrupt file");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/disputes", { order_id: order.id, issue_type: type, description: desc });
      toast.success("Dispute raised — seller payout frozen"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid={`dispute-${order.id}`} className="flex items-center gap-1.5 border border-red-500/40 text-red-600 rounded-md px-3 py-2 text-sm hover:bg-red-500/5"><MessageSquareWarning className="h-4 w-4" /> Report</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Report a problem</DialogTitle></DialogHeader>
        <label className="text-sm font-medium">Issue type</label>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="dispute-type" className="w-full border border-border rounded-md p-2.5 text-sm bg-card">
          {["corrupt file", "wrong file", "mismatched design", "not as described", "technical issue", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="dispute-desc" placeholder="Describe the issue…" className="w-full border border-border rounded-md p-3 text-sm bg-card" rows={4} />
        <DialogFooter><button onClick={submit} disabled={loading || !desc} data-testid="submit-dispute" className="flex items-center gap-2 bg-red-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit dispute</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrintToRealityTeaser() {
  return (
    <div
      data-testid="p2r-teaser"
      className="relative overflow-hidden rounded-lg border border-emerald-500/30 text-white p-5 sm:p-6"
      style={{ background: "linear-gradient(135deg, #052e3f 0%, #084a68 55%, #0b6787 100%)" }}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="h-11 w-11 shrink-0 rounded-md bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
          <Printer className="h-5 w-5 text-emerald-300" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300 mb-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Coming soon
          </div>
          <p className="font-display font-bold text-base sm:text-lg leading-tight">Print to Reality — 3D print vendors onboarding now.</p>
          <p className="mt-1 text-xs sm:text-sm text-slate-200 leading-relaxed max-w-xl">
            Soon you'll be able to send any purchased CAD straight to a nearby 3D print shop for wax/resin casting — quoted, paid, and tracked right here on Jeweltix. No offline hunting.
          </p>
        </div>
        <a
          href="/vendor/apply"
          data-testid="teaser-vendor-cta"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white px-4 py-2 text-xs font-semibold transition-colors"
        >
          Know a shop? Refer them <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>
    </div>
  );
}



function PrintJobsTab({ jobs, onReload }) {
  if (jobs.length === 0) {
    return (
      <div className="border border-dashed border-border rounded-md py-16 text-center" data-testid="buyer-jobs-empty">
        <Printer className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">You haven't sent any print jobs yet.</p>
        <p className="text-xs text-muted-foreground mt-1">Purchase a design, then hit the "Print" button to route it to a nearby vendor.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {jobs.map((j) => (
        <BuyerJobCard key={j.id} job={j} onReload={onReload} />
      ))}
    </div>
  );
}

function BuyerJobCard({ job, onReload }) {
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const { openCheckout } = useRazorpay();

  /** Buyer confirms the vendor's quote → real Razorpay charge for the quote
   *  amount → on success, backend flips job to `confirmed` and computes the
   *  vendor settlement (20% margin + ~2% gateway → net into escrow for 48h). */
  const payAndConfirm = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post(`/print-jobs/${job.id}/create-payment-order`);
      const success = await openCheckout({
        order,
        name: "Jeweltix · Print Job",
        description: `${job.listing?.title || "Print job"} · ${job.vendor?.shop_name || ""} · ₹${(order.amount / 100).toFixed(0)}`,
      });
      await api.post(`/print-jobs/${job.id}/verify-payment`, success);
      logTermsCheckpoint({
        role: "buyer",
        event: "confirm_quote",
        contextId: job.id,
        checkboxes: ["buyer_terms", "privacy"],
      });
      toast.success("Payment successful — vendor has been notified to start printing.");
      onReload();
    } catch (err) {
      if (err?.code === "cancelled") toast.info("Payment cancelled — no charge was made.");
      else if (err?.code === "failed") toast.error(err.message || "Payment failed. Please try again.");
      else toast.error(apiErr(err) || err?.message || "Payment could not be completed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this print job? The vendor will be notified.")) return;
    setBusy(true);
    try {
      await api.post(`/print-jobs/${job.id}/cancel`);
      toast.success("Job cancelled.");
      onReload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-md bg-card p-4 sm:p-5" data-testid={`buyer-job-${job.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold text-sm">{job.listing?.title || "Print job"}</p>
        <span className={`text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 ${JOB_STATUS_STYLE[job.status] || ""}`} data-testid={`buyer-job-status-${job.id}`}>
          {job.status}
        </span>
        {job.dispute_id && (
          <span className="text-[10px] font-mono uppercase tracking-widest border rounded-full px-2 py-0.5 bg-red-500/15 text-red-500 border-red-500/30">
            Disputed
          </span>
        )}
        {job.tracking_number && (
          <span className="text-[10px] font-mono border rounded-full px-2 py-0.5 border-border inline-flex items-center gap-1">
            <Truck className="h-3 w-3" /> {job.tracking_number}
          </span>
        )}
      </div>
      <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
        Job #{job.id.slice(0, 8)} · Sent to {job.vendor?.shop_name || "vendor"}{job.vendor?.city ? ` (${job.vendor.city})` : ""} · {new Date(job.created_at).toLocaleDateString()}
      </p>

      <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
        <span className="flex items-center gap-1.5">
          {job.delivery_mode === "delivery" ? <Truck className="h-3.5 w-3.5" /> : <PackageCheck className="h-3.5 w-3.5" />}
          {job.delivery_mode === "delivery" ? "Delivery" : "Pickup"}
        </span>
        {job.delivery_mode === "delivery" && job.delivery_address && (
          <span className="sm:col-span-2 flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 shrink-0" /> {job.delivery_address}
          </span>
        )}
      </div>

      {/* Money row */}
      <div className="mt-3 flex flex-wrap gap-4 text-xs items-center">
        {job.approx_estimate != null && !job.vendor_quote && (
          <span><span className="text-muted-foreground">Rough estimate:</span> <strong className="ml-1 font-mono">{inr(job.approx_estimate)}</strong></span>
        )}
        {job.vendor_quote != null && (
          <span><span className="text-muted-foreground">Vendor quote:</span> <strong className="ml-1 font-display text-primary">{inr(job.vendor_quote)}</strong></span>
        )}
      </div>

      {/* Action row */}
      <div className="mt-3 flex flex-wrap gap-2">
        {job.status === "new" && (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-500/30 bg-blue-500/5 text-blue-600 text-xs font-medium px-3 py-1.5">
              <Clock className="h-3.5 w-3.5" /> Waiting for vendor to accept
            </span>
            <button onClick={cancel} disabled={busy} data-testid={`buyer-cancel-${job.id}`} className="border border-red-500/30 text-red-500 hover:bg-red-500/5 rounded-md px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </button>
          </>
        )}
        {job.status === "accepted" && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-indigo-500/30 bg-indigo-500/5 text-indigo-600 text-xs font-medium px-3 py-1.5">
            <Clock className="h-3.5 w-3.5" /> Vendor accepted — waiting for their quote
          </span>
        )}
        {job.status === "quoted" && (
          <>
            <div className="w-full">
              <p className="text-[11px] text-muted-foreground leading-snug border-l-2 border-primary/40 pl-2 mb-2" data-testid={`buyer-quote-consent-${job.id}`}>
                By confirming, you accept the <a href="/terms/buyer" target="_blank" rel="noreferrer" className="text-primary underline">Buyer Terms</a> and acknowledge that Jeweltix does not warrant print quality — the printing contract is between you and the vendor.
              </p>
            </div>
            <button onClick={payAndConfirm} disabled={busy} data-testid={`buyer-pay-${job.id}`} className="bg-primary text-primary-foreground rounded-md px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Pay {inr(job.vendor_quote)} & confirm
            </button>
            <button onClick={cancel} disabled={busy} data-testid={`buyer-decline-${job.id}`} className="border border-red-500/30 text-red-500 hover:bg-red-500/5 rounded-md px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5">
              <XCircle className="h-3.5 w-3.5" /> Decline & pick another vendor
            </button>
            <span className="text-[10px] font-mono uppercase tracking-widest bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 rounded-full px-2 py-1">
              Escrow-protected — 48h hold
            </span>
          </>
        )}
        {job.status === "confirmed" && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 text-emerald-600 text-xs font-medium px-3 py-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Paid — vendor is preparing to print
          </span>
        )}
        {["printing", "ready"].includes(job.status) && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-purple-500/30 bg-purple-500/5 text-purple-600 text-xs font-medium px-3 py-1.5">
            <Clock className="h-3.5 w-3.5" /> {job.status === "printing" ? "Vendor is printing your part" : "Ready for handover"}
          </span>
        )}
        {["shipped", "completed"].includes(job.status) && !job.dispute_id && (
          <>
            <span className={`inline-flex items-center gap-1.5 rounded-md border text-xs font-medium px-3 py-1.5 ${job.status === "completed" ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-600" : "border-sky-500/30 bg-sky-500/5 text-sky-600"}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> {job.status === "completed" ? "Completed" : "In transit"}
            </span>
            <button
              onClick={() => setDisputeOpen(true)}
              data-testid={`buyer-dispute-${job.id}`}
              className="border border-amber-500/40 text-amber-600 hover:bg-amber-500/5 rounded-md px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5"
            >
              <MessageSquareWarning className="h-3.5 w-3.5" /> Raise a dispute
            </button>
          </>
        )}
        {job.status === "declined" && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 text-red-500 text-xs font-medium px-3 py-1.5">
            <XCircle className="h-3.5 w-3.5" /> Vendor declined
          </span>
        )}
        {job.dispute_id && (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/5 text-red-500 text-xs font-medium px-3 py-1.5">
            <MessageSquareWarning className="h-3.5 w-3.5" /> Dispute open — our team is reviewing, escrow paused
          </span>
        )}
      </div>

      <PrintJobChat jobId={job.id} me="buyer" />
      {disputeOpen && (
        <PrintJobDisputeModal jobId={job.id} onDone={() => { setDisputeOpen(false); onReload(); }} onClose={() => setDisputeOpen(false)} />
      )}
    </div>
  );
}

function PrintJobDisputeModal({ jobId, onDone, onClose }) {
  const [issueType, setIssueType] = useState("bad_quality");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (description.trim().length < 10) return toast.error("Describe the issue in at least 10 characters.");
    setBusy(true);
    try {
      await api.post(`/print-jobs/${jobId}/disputes`, { issue_type: issueType, description });
      toast.success("Dispute opened — our team will review shortly. The vendor's escrow release is now paused.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const options = [
    { key: "bad_quality", label: "Bad print quality" },
    { key: "wrong_output", label: "Wrong output / doesn't match design" },
    { key: "damaged", label: "Part arrived damaged" },
    { key: "never_delivered", label: "Never delivered" },
    { key: "other", label: "Something else" },
  ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquareWarning className="h-5 w-5 text-amber-500" /> Raise a dispute</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Filing a dispute pauses the vendor's payout until our team reviews it. Choose the closest issue and describe what went wrong.
        </p>
        <div className="space-y-2 mt-3">
          {options.map((o) => (
            <label key={o.key} className={`flex items-center gap-3 border rounded-md p-2.5 cursor-pointer transition-colors ${issueType === o.key ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
              <input type="radio" checked={issueType === o.key} onChange={() => setIssueType(o.key)} className="accent-primary" data-testid={`dispute-type-${o.key}`} />
              <span className="text-sm">{o.label}</span>
            </label>
          ))}
        </div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          data-testid="dispute-description"
          placeholder="e.g. Layer lines visible on prong tips; buyer refused the part. Photos to follow via chat."
          className="mt-3 w-full border border-border rounded-md p-2.5 text-sm bg-background"
        />
        <DialogFooter>
          <button onClick={onClose} className="border border-border rounded-md px-4 py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="dispute-submit" className="bg-amber-600 hover:bg-amber-500 text-white rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} File dispute
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
